import { create } from 'zustand';

import { IShipment, TContractState } from '../models/shipment';

export type TShipmentsSortBy = 'name' | 'createdAt' | 'updatedAt';

interface IShipmentsStore {
  rowData: IShipment[];
  pageSize: number;
  offset: number;
  getAll: boolean;
  sortBy: TShipmentsSortBy;
  order: 'ASC' | 'DESC';
  defaultSort: {
    sortBy: TShipmentsSortBy;
    order: 'ASC' | 'DESC';
  };
  error: string | boolean;
  loading: boolean;
  count: number;
  dispatch: (args: TShipmentsReducerInput) => void;
  total: number;
  contractStatus: TContractState[] | null;
  rts: boolean | null;
  incidentFilter: string | null;
  partnerAutocomplete?: [{ id: string; name: string }] | null;
  searchTerms: {
    boxId: string;
    lpui: string;
    id: string;
    partnerId: string;
    recipientLastName: string;
    recipientEmail: string;
    senderLastName: string;
    trackingCode: string;
    activeFrom: number | undefined;
    activeTo: number | undefined;
    activeState: string;
  };
  forceRefresh: number;
}

type TShipmentsReducerInput =
  | {
      type: 'updateRowData';
      args: { rowData: any[] };
    }
  | {
      type: 'updateError';
      args: { error: string | boolean };
    }
  | {
      type: 'updateLoading';
      args: { loading: boolean };
    }
  | {
      type: 'updatePagination';
      args: { pageSize: number; offset: number };
    }
  | {
      type: 'updateSorting';
      args: {
        sortBy: TShipmentsSortBy;
        order: 'ASC' | 'DESC';
      };
    }
  | {
      type: 'updateFilters';
      args: {
        contractStatus: TContractState[] | null;
        deliveryStatus: boolean | null;
        incidentFilter: string | null;
      };
    }
  | {
      type: 'refreshTable';
    }
  | {
      type: 'updateCount';
      args: { count: number };
    }
  | {
      type: 'updateTotal';
      args: { total: number };
    }
  | {
      type: 'updateDefaultSort';
    }
  //TODO Rework this to be more specific
  | {
      type: 'updatePartner';
      args: { partnerAutocomplete: [{ id: string; name: string }] | null };
    }
  | {
      type: 'updateSearchTerms';
      args: {
        boxId: string;
        lpui: string;
        id: string;
        partnerId: string;
        recipientLastName: string;
        recipientEmail: string;
        senderLastName: string;
        trackingCode: string;
        activeFrom: number | undefined;
        activeTo: number | undefined;
        activeState: string;
      };
    }
  | {
      type: 'reset';
    }
  | {
      type: 'overrideSearchContext';
      args: {
        searchTerms: IShipmentsStore['searchTerms'];
        partnerAutocomplete?: [{ id: string; name: string }] | null;
      };
    };

export const createEmptyShipmentSearchTerms =
  (): IShipmentsStore['searchTerms'] => ({
    boxId: '',
    lpui: '',
    id: '',
    partnerId: '',
    recipientLastName: '',
    recipientEmail: '',
    senderLastName: '',
    trackingCode: '',
    activeFrom: undefined,
    activeTo: undefined,
    activeState: '',
  });

const createInitialState = (): Omit<IShipmentsStore, 'dispatch'> => ({
  rowData: [],
  error: '',
  loading: false,
  pageSize: 25,
  offset: 0,
  getAll: true,
  sortBy: 'updatedAt',
  order: 'DESC',
  defaultSort: {
    sortBy: 'updatedAt',
    order: 'DESC',
  },
  count: 0,
  total: 0,
  contractStatus: null,
  rts: null,
  incidentFilter: null,
  partnerAutocomplete: null,
  searchTerms: createEmptyShipmentSearchTerms(),
  forceRefresh: 0,
});

const lpShipmentsReducer = (
  state: IShipmentsStore,
  input: TShipmentsReducerInput
) => {
  switch (input.type) {
    case 'updateRowData':
      return { ...state, rowData: input.args.rowData };
    case 'updateError':
      return { ...state, error: input.args.error };
    case 'updateLoading':
      return { ...state, loading: input.args.loading };
    case 'updatePagination':
      return {
        ...state,
        pageSize: input.args.pageSize,
        offset: input.args.offset,
      };
    case 'updateSorting':
      return { ...state, sortBy: input.args.sortBy, order: input.args.order };
    case 'updateCount':
      return { ...state, count: input.args.count };
    case 'refreshTable':
      return {
        ...state,
        pageSize:
          state.pageSize % 5 === 0 ? state.pageSize + 1 : state.pageSize - 1,
      };
    case 'updateFilters': {
      return {
        ...state,
        contractStatus: input.args.contractStatus,
        rts: input.args.deliveryStatus,
        incidentFilter: input.args.incidentFilter,
        getAll: input.args.contractStatus === null,
      };
    }
    case 'updateTotal':
      return { ...state, total: input.args.total };
    case 'updateDefaultSort':
      return {
        ...state,
        defaultSort: { sortBy: state.sortBy, order: state.order },
      };
    case 'updateSearchTerms':
      return {
        ...state,
        searchTerms: input.args,
        offset: 0,
      };
    case 'updatePartner':
      return { ...state, partnerAutocomplete: input.args.partnerAutocomplete };
    case 'reset':
      return { ...state, ...createInitialState() };

    case 'overrideSearchContext': {
      const initialState = createInitialState();

      return {
        ...state,
        ...initialState,
        searchTerms: input.args.searchTerms,
        partnerAutocomplete:
          input.args.partnerAutocomplete ?? initialState.partnerAutocomplete,
      };
    }
  }
};

const useShipmentsStore = create<IShipmentsStore>((set) => ({
  ...createInitialState(),
  dispatch: (input) => set((state) => lpShipmentsReducer(state, input)),
}));

export const searchTermsSelector = (state: IShipmentsStore) =>
  state.searchTerms;
export const dispatchSelector = (state: IShipmentsStore) => state.dispatch;

export default useShipmentsStore;
